/**
 * Campaign CSV audience ingest contracts (Order 30B.2).
 * Engineering ingest limits — NOT Meta messaging capacity.
 */
import { z } from "zod";
import { CAMPAIGN_CSV_INGEST_LIMITS } from "./campaign-contracts.js";
export const CAMPAIGN_AUDIENCE_IMPORT_STATUSES = [
    "UPLOADED",
    "PARSING",
    "AWAITING_CONFIGURATION",
    "READY",
    "FAILED",
    "EXPIRED",
    "DELETED",
    /** Successfully materialised into CampaignRecipient; source file retired. */
    "CONSUMED",
];
export const CAMPAIGN_AUDIENCE_CSV_HEADER_MODES = [
    "MULTI_COLUMN",
    "SINGLE_COLUMN_WITH_HEADER",
    "SINGLE_COLUMN_HEADERLESS",
];
export const CAMPAIGN_AUDIENCE_UPLOAD_ISSUE_REASONS = [
    "MISSING_PHONE",
    "INVALID_PHONE",
    "MISSING_COUNTRY",
    "DUPLICATE_RECIPIENT",
    "DUPLICATE_RECIPIENT_CONFLICT",
    "UPLOAD_ROW_INVALID",
];
export const CAMPAIGN_AUDIENCE_CSV_ERROR_CODES = [
    "CAMPAIGN_AUDIENCE_FILE_TOO_LARGE",
    "CAMPAIGN_AUDIENCE_INVALID_FILE_TYPE",
    "CAMPAIGN_AUDIENCE_ROW_LIMIT_EXCEEDED",
    "CAMPAIGN_AUDIENCE_COLUMN_LIMIT_EXCEEDED",
    "CAMPAIGN_AUDIENCE_HEADER_INVALID",
    "CAMPAIGN_AUDIENCE_HEADER_DUPLICATE",
    "CAMPAIGN_AUDIENCE_VALUE_TOO_LONG",
    "CAMPAIGN_AUDIENCE_HEADERS_REQUIRED",
    "CAMPAIGN_AUDIENCE_HEADER_CONFIRMATION_REQUIRED",
    "CAMPAIGN_AUDIENCE_PHONE_COLUMN_REQUIRED",
    "CAMPAIGN_AUDIENCE_UPLOAD_NOT_FOUND",
    "CAMPAIGN_AUDIENCE_UPLOAD_NOT_READY",
    "CAMPAIGN_AUDIENCE_UPLOAD_EXPIRED",
    "CAMPAIGN_AUDIENCE_UPLOAD_CONSUMED",
    "CAMPAIGN_AUDIENCE_EMPTY",
    "CAMPAIGN_AUDIENCE_COMMIT_IN_PROGRESS",
    "CAMPAIGN_AUDIENCE_IMPORT_FAILED",
    "STORAGE_QUOTA_EXCEEDED",
];
export const CAMPAIGN_AUDIENCE_PHONE_COLUMN_ALIASES = [
    "phone",
    "mobile",
    "telephone",
    "tel",
    "phone_number",
    "mobile_number",
    "טלפון",
    "נייד",
];
const PHONE_ALIAS_SET = new Set(CAMPAIGN_AUDIENCE_PHONE_COLUMN_ALIASES.map((a) => normalizeHeaderKey(a)));
/**
 * Deterministic internal key from a display header.
 * Preserves letters (including Hebrew); collapses punctuation/whitespace to `_`.
 */
export function normalizeHeaderKey(displayName) {
    const nfc = displayName.normalize("NFC").trim().toLowerCase();
    const replaced = nfc
        .replace(/[^\p{L}\p{N}]+/gu, "_")
        .replace(/_+/g, "_")
        .replace(/^_|_$/g, "");
    if (replaced.length === 0) {
        return "column";
    }
    if (/^\d/.test(replaced)) {
        return `col_${replaced}`;
    }
    return replaced;
}
export function buildHeaderDescriptors(displayNames, limits = {
    maxColumns: CAMPAIGN_CSV_INGEST_LIMITS.maxColumns,
    maxHeaderLength: CAMPAIGN_CSV_INGEST_LIMITS.maxHeaderLength,
}) {
    if (displayNames.length === 0) {
        return {
            ok: false,
            code: "CAMPAIGN_AUDIENCE_HEADER_INVALID",
            message: "CSV header row is empty",
        };
    }
    if (displayNames.length > limits.maxColumns) {
        return {
            ok: false,
            code: "CAMPAIGN_AUDIENCE_HEADER_INVALID",
            message: `CSV exceeds max columns (${limits.maxColumns})`,
        };
    }
    const seen = new Map();
    const headers = [];
    for (let index = 0; index < displayNames.length; index++) {
        const displayName = displayNames[index].trim();
        if (displayName.length === 0) {
            return {
                ok: false,
                code: "CAMPAIGN_AUDIENCE_HEADER_INVALID",
                message: `CSV header at column ${index + 1} is empty`,
            };
        }
        if (displayName.length > limits.maxHeaderLength) {
            return {
                ok: false,
                code: "CAMPAIGN_AUDIENCE_HEADER_INVALID",
                message: `CSV header exceeds max length (${limits.maxHeaderLength})`,
            };
        }
        const key = normalizeHeaderKey(displayName);
        const prior = seen.get(key);
        if (prior !== undefined) {
            return {
                ok: false,
                code: "CAMPAIGN_AUDIENCE_HEADER_DUPLICATE",
                message: `Duplicate CSV headers normalize to the same key: "${prior}" / "${displayName}"`,
            };
        }
        seen.set(key, displayName);
        headers.push({ version: 1, index, displayName, key });
    }
    return { ok: true, headers: { version: 1, headers } };
}
/** Suggest a phone column only when exactly one clear alias candidate exists. */
export function suggestPhoneColumnKey(headers) {
    const candidates = headers.filter((h) => PHONE_ALIAS_SET.has(h.key) || PHONE_ALIAS_SET.has(normalizeHeaderKey(h.displayName)));
    if (candidates.length === 1) {
        return candidates[0].key;
    }
    return null;
}
const E164_LIKE = /^\+?[0-9][0-9\s().-]{5,}$/;
/**
 * Heuristic for single-column files: is the first cell more like a header or phone data?
 * Returns null when ambiguous — caller must require explicit hasHeader.
 */
export function detectSingleColumnHasHeader(firstCell) {
    const trimmed = firstCell.trim();
    if (trimmed.length === 0) {
        return null;
    }
    const key = normalizeHeaderKey(trimmed);
    if (PHONE_ALIAS_SET.has(key)) {
        return true;
    }
    if (E164_LIKE.test(trimmed) || /^\d{7,}$/.test(trimmed.replace(/[\s()-]/g, ""))) {
        return false;
    }
    // Non-phone text that is not a known alias — ambiguous
    return null;
}
export const campaignAudienceCsvHeaderDescriptorSchema = z.object({
    version: z.literal(1),
    index: z.number().int().nonnegative(),
    displayName: z.string().min(1).max(CAMPAIGN_CSV_INGEST_LIMITS.maxHeaderLength),
    key: z.string().min(1).max(128),
});
export const campaignAudienceCsvHeadersSchema = z.object({
    version: z.literal(1),
    headers: z.array(campaignAudienceCsvHeaderDescriptorSchema).max(CAMPAIGN_CSV_INGEST_LIMITS.maxColumns),
});
export const configureCampaignAudienceUploadRequestSchema = z.object({
    phoneColumnKey: z.string().min(1).max(128).optional(),
    defaultCountryIso: z
        .string()
        .length(2)
        .regex(/^[A-Za-z]{2}$/)
        .transform((v) => v.toUpperCase())
        .nullable()
        .optional(),
    /** Explicit confirmation for ambiguous single-column header vs data. */
    hasHeader: z.boolean().optional(),
});
export const campaignAudienceUploadIssueSampleSchema = z.object({
    reason: z.enum(CAMPAIGN_AUDIENCE_UPLOAD_ISSUE_REASONS),
    rowNumber: z.number().int().positive(),
    message: z.string().max(200).optional(),
});
export const campaignAudienceUploadPreviewRowSchema = z.object({
    rowNumber: z.number().int().positive(),
    normalizedPhoneE164: z.string().nullable(),
    uploadFields: z.record(z.string(), z.string()),
    issueReason: z.enum(CAMPAIGN_AUDIENCE_UPLOAD_ISSUE_REASONS).nullable(),
});
export const campaignAudienceUploadPreviewSummarySchema = z.object({
    version: z.literal(1),
    sourceRowCount: z.number().int().nonnegative(),
    validRowCount: z.number().int().nonnegative(),
    invalidRowCount: z.number().int().nonnegative(),
    duplicateRowCount: z.number().int().nonnegative(),
    conflictRowCount: z.number().int().nonnegative(),
    byReason: z.record(z.string(), z.number().int().nonnegative()),
});
export const campaignAudienceUploadDtoSchema = z.object({
    uploadId: z.string().min(1),
    status: z.enum(CAMPAIGN_AUDIENCE_IMPORT_STATUSES),
    originalFileName: z.string(),
    byteSize: z.number().int().nonnegative(),
    headerMode: z.enum(CAMPAIGN_AUDIENCE_CSV_HEADER_MODES).nullable(),
    headers: campaignAudienceCsvHeadersSchema.nullable(),
    phoneColumnKey: z.string().nullable(),
    phoneColumnSuggested: z.string().nullable(),
    phoneColumnSelectionRequired: z.boolean(),
    defaultCountryIso: z.string().nullable(),
    hasHeader: z.boolean().nullable(),
    headerConfirmationRequired: z.boolean(),
    sourceRowCount: z.number().int().nonnegative().nullable(),
    processedRowCount: z.number().int().nonnegative(),
    errorCode: z.string().nullable(),
    errorSummary: z.string().nullable(),
    expiresAt: z.string().datetime({ offset: true }),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
});
export const campaignAudienceUploadResponseSchema = z.object({
    campaign: z.object({
        id: z.number().int().positive(),
        audienceDefinitionRevision: z.number().int().positive(),
        snapshotStale: z.boolean(),
    }),
    upload: campaignAudienceUploadDtoSchema.nullable(),
});
export const campaignAudienceUploadPreviewResponseSchema = z.object({
    upload: campaignAudienceUploadDtoSchema,
    summary: campaignAudienceUploadPreviewSummarySchema.nullable(),
    sampleRows: z.array(campaignAudienceUploadPreviewRowSchema),
    issueSamples: z.array(campaignAudienceUploadIssueSampleSchema),
});
export const CAMPAIGN_AUDIENCE_UPLOAD_PREVIEW_SAMPLE_LIMIT = 50;
//# sourceMappingURL=campaign-audience-csv.js.map